import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useGame } from '../context/GameContext';
import { saveTrivia } from '../services/api';
import { getQuestions } from '../data/getQuestions';
import ProgressBar from '../components/ProgressBar';
import QuestionCard from '../components/QuestionCard';
import FeedbackOverlay from '../components/FeedbackOverlay';
import { TriviaAnswer } from '../types';

const Trivia: React.FC = () => {
  const navigate = useNavigate();
  const { sessionId, participantData, addTriviaAnswer, setTriviaScore, setNickname: setContextNickname, setPhase } = useGame();
  const { t } = useTranslation('trivia');
  const questions = getQuestions(t);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState<TriviaAnswer[]>([]);
  const [showFeedback, setShowFeedback] = useState(false);
  const [lastAnswer, setLastAnswer] = useState<{ isCorrect: boolean; feedback: string } | null>(null);

  const startedAt = useRef(new Date().toISOString());
  const questionStartTime = useRef(Date.now());

  const currentQuestion = questions[currentIndex];
  const isLastQuestion = currentIndex === questions.length - 1;

  useEffect(() => {
    questionStartTime.current = Date.now();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentIndex]);

  const handleAnswer = (selectedIndex: number) => {
    const timeMs = Date.now() - questionStartTime.current;
    const isCorrect = selectedIndex === currentQuestion.correct_index;

    const answer: TriviaAnswer = {
      question_id: currentQuestion.id,
      selected: selectedIndex,
      correct: isCorrect,
      time_ms: timeMs,
    };

    setAnswers((prev) => [...prev, answer]);
    addTriviaAnswer(answer);
    setLastAnswer({
      isCorrect,
      feedback: isCorrect ? currentQuestion.feedback_correct : currentQuestion.feedback_incorrect,
    });
    setShowFeedback(true);
  };

  const handleContinue = async () => {
    setShowFeedback(false);

    if (isLastQuestion) {
      await finishTrivia([...answers]);
    } else {
      setCurrentIndex((prev) => prev + 1);
    }
  };

  const finishTrivia = async (finalAnswers: TriviaAnswer[]) => {
    if (!sessionId) {
      navigate('/');
      return;
    }

    const score = finalAnswers.filter((a) => a.correct).length;
    const completedAt = new Date().toISOString();
    const autoNickname = participantData?.nombre_completo
      ? participantData.nombre_completo.split(' ')[0]
      : (participantData?.email?.split('@')[0] || t('fallbackNickname'));

    setTriviaScore(score);
    setContextNickname(autoNickname);

    try {
      await saveTrivia(sessionId, {
        score,
        answers: finalAnswers,
        nickname: autoNickname,
        started_at: startedAt.current,
        completed_at: completedAt,
      });
    } catch (error) {
      console.error('Error saving trivia:', error);
    }

    setPhase('result');
    navigate('/resultado');
  };

  return (
    <div
      className="min-h-screen flex flex-col px-6 py-8"
      style={{ background: 'linear-gradient(135deg, #00082f 0%, #001055 60%, #00082f 100%)' }}
    >
      <div className="w-full max-w-sm mx-auto flex flex-col gap-6">
        <div className="pt-2">
          <ProgressBar current={currentIndex + 1} total={questions.length} />
        </div>

        <QuestionCard
          key={currentIndex}
          question={currentQuestion}
          onAnswer={handleAnswer}
        />
      </div>

      {showFeedback && lastAnswer && (
        <FeedbackOverlay
          isCorrect={lastAnswer.isCorrect}
          feedback={lastAnswer.feedback}
          onContinue={handleContinue}
          isLast={isLastQuestion}
        />
      )}
    </div>
  );
};

export default Trivia;
